import { format, subDays } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import type { TaskAgenda } from '@/types/taskAgenda';
import { clampTaskAgendaRangeToCurrentMonthSp, nextPayloadWithAutoSlot } from '@/services/taskAgenda/taskAgendaSchedule';
import { executeReprocessamentoRegrasDesmembramentoJob } from '@/services/taskAgenda/executeReprocessamentoRegrasDesmembramentoJob';

export type RunAgendaRegrasDesmembramentoOptions = {
  automaticSlotKey?: string;
  /** Alíquota decimal (ex.: 0,13) para recalcular imposto no reprocessamento. */
  impostoPercentual?: number;
};

function buildRegrasJobParams(agenda: TaskAgenda) {
  const p = agenda.payload || {};
  const days = typeof p.regrasDaysRange === 'number' && p.regrasDaysRange > 0 && p.regrasDaysRange <= 3650 ? p.regrasDaysRange : null;
  if (days) {
    const end = new Date();
    const start = subDays(end, days);
    const { dataInicio, dataFim } = clampTaskAgendaRangeToCurrentMonthSp(
      format(start, 'yyyy-MM-dd'),
      format(end, 'yyyy-MM-dd')
    );
    return {
      dataInicio,
      dataFim,
      batchSize: typeof p.regrasBatchSize === 'number' ? Math.floor(p.regrasBatchSize) : undefined,
    };
  }
  const di = typeof p.regrasDataInicio === 'string' && p.regrasDataInicio.trim() ? p.regrasDataInicio.trim() : null;
  const df = typeof p.regrasDataFim === 'string' && p.regrasDataFim.trim() ? p.regrasDataFim.trim() : null;
  const clamped = clampTaskAgendaRangeToCurrentMonthSp(di, df);
  return {
    dataInicio: clamped.dataInicio,
    dataFim: clamped.dataFim,
    batchSize: typeof p.regrasBatchSize === 'number' ? Math.floor(p.regrasBatchSize) : undefined,
  };
}

/**
 * Reprocessa regras de desmembramento (reclassificação): soma `horas_totais` no mês por bucket de regra
 * para decidir cliente filho (ex. FIXO vs EXTRA); recalcula faturamento/imposto com valor/hora da regra.
 */
export async function runAgendaRegrasDesmembramento(
  agenda: TaskAgenda,
  options?: RunAgendaRegrasDesmembramentoOptions
): Promise<void> {
  const automaticSlotKey = options?.automaticSlotKey;
  const nextPayload = () => nextPayloadWithAutoSlot(agenda, automaticSlotKey);
  const jobParams = {
    ...buildRegrasJobParams(agenda),
    impostoPercentual: options?.impostoPercentual,
  };

  const { data: runRow, error: insErr } = await supabase
    .from('task_agenda_runs')
    .insert({
      agenda_id: agenda.id,
      status: 'running',
      summary: null,
      error_message: null,
    })
    .select('id')
    .single();

  if (insErr || !runRow?.id) {
    const msg = insErr?.message || 'insert sem id';
    console.error('[task_agenda] Falha ao registrar execução (regras desmembramento):', insErr);
    throw new Error(`Não foi possível registrar a execução na agenda: ${msg}`);
  }

  const runId = runRow.id as string;

  try {
    const result = await executeReprocessamentoRegrasDesmembramentoJob(jobParams);

    if (result.total === 0) {
      const summary = 'Nenhuma ordem de serviço encontrada com os filtros atuais.';
      await supabase
        .from('task_agenda_runs')
        .update({
          finished_at: new Date().toISOString(),
          status: 'success',
          summary,
          error_message: null,
        })
        .eq('id', runId);
      await supabase
        .from('task_agenda')
        .update({
          last_run_at: new Date().toISOString(),
          last_run_status: 'success',
          last_run_summary: summary,
          payload: nextPayload(),
        })
        .eq('id', agenda.id);
      return;
    }

    const summary = `OS analisadas: ${result.total}; regras aplicadas: ${result.regrasAplicadas}; erros: ${result.errors}`;
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'success',
        summary,
        error_message: null,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'success',
        last_run_summary: summary,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
  } catch (e: unknown) {
    const msg = e instanceof Error ? e.message : String(e);
    await supabase
      .from('task_agenda_runs')
      .update({
        finished_at: new Date().toISOString(),
        status: 'error',
        summary: null,
        error_message: msg,
      })
      .eq('id', runId);
    await supabase
      .from('task_agenda')
      .update({
        last_run_at: new Date().toISOString(),
        last_run_status: 'error',
        last_run_summary: msg,
        payload: nextPayload(),
      })
      .eq('id', agenda.id);
    throw e;
  }
}
